import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { queryKeysCompany } from '~/data/company/queryKeys';

import { queryKeysCompanyAccount } from '../queryKeys';

import { putCompanyAccountSupplierLetter } from './putCompanyAccountSupplierLetter';
import {
  type PutCompanyAccountSupplierLetterPayload,
  type SupplierLetterMeta,
} from './types';

/**
 * Custom hook for uploading or replacing the supplier authorization letter for a CompanyAccount.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Supplier-Letter/operation/put_company_account_supplier_letter_vadmin_company_accounts__company_account_id__supplier_letter_put
 */
export const useMutationPutCompanyAccountSupplierLetter = (
  options?: Omit<
    UseMutationOptions<
      SupplierLetterMeta,
      Error,
      PutCompanyAccountSupplierLetterPayload
    >,
    'mutationFn' | 'onSuccess'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    SupplierLetterMeta,
    Error,
    PutCompanyAccountSupplierLetterPayload
  >({
    async mutationFn({ companyAccountId, body }) {
      return putCompanyAccountSupplierLetter(companyAccountId, body);
    },
    async onSuccess(_data, { companyAccountId }) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeysCompanyAccount.getSupplierLetter(companyAccountId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysCompany.getSupplierLetters(),
        }),
      ]);
    },
    ...options,
  });
};
